// SpendReport.tsx — page: what was spent on sheet leads and what it bought (ADMIN ONLY).
//
// Route it next to Sheet Imports, e.g.
//   <Route path="/leads/spend-report" element={<SpendReport />} />
// The API returns 403 for staff; the page then shows "Admins only".

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, IndianRupee, Lock, RefreshCw, TriangleAlert } from 'lucide-react';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import { fmtINR, spendError } from './parts/spendTypes';
import { Banner, StatTile, TONES } from '../SheetsManager/parts/SheetImportDetailsModal';

const SHEET_IMPORTS_PATH = '/leads/sheet-imports';

type GroupBy = 'campaign' | 'sheet' | 'sync';
type Preset = 'month' | 'last_month' | '30d' | '90d' | 'all' | 'custom';

interface ReportRow {
  key: string;
  label: string;
  sublabel?: string;
  amount_incl_gst_paise: number;
  amount_excl_gst_paise: number;
  leads: number;
  reached: number;
  qualified: number;
  converted: number;
  invalid: number;
  syncs: number;
}
interface WeekPoint {
  week_start: string;
  amount_incl_gst_paise: number;
  amount_excl_gst_paise: number;
  leads: number;
}
interface ReportData {
  group_by: GroupBy;
  rows: ReportRow[];
  totals: ReportRow;
  weeks: WeekPoint[];
  syncs_without_amount: number;
}

// ── Dates (IST calendar days, YYYY-MM-DD) ────────────────────────────────────
const istDay = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const istParts = () => {
  const [y, m, d] = istDay(new Date()).split('-').map(Number);
  return { y, m, d };
};
const ymd = (y: number, m: number, d: number) => istDay(new Date(Date.UTC(y, m - 1, d, 6)));
const daysAgo = (n: number) => istDay(new Date(Date.now() - n * 86400000));

const presetRange = (p: Preset): { from: string; to: string } => {
  const { y, m } = istParts();
  const today = istDay(new Date());
  switch (p) {
    case 'month':
      return { from: ymd(y, m, 1), to: today };
    case 'last_month':
      return { from: ymd(m === 1 ? y - 1 : y, m === 1 ? 12 : m - 1, 1), to: ymd(y, m, 0) };
    case '30d':
      return { from: daysAgo(29), to: today };
    case '90d':
      return { from: daysAgo(89), to: today };
    default:
      return { from: '', to: '' };
  }
};

const PRESETS: { v: Preset; l: string }[] = [
  { v: 'month', l: 'This month' },
  { v: 'last_month', l: 'Last month' },
  { v: '30d', l: '30 days' },
  { v: '90d', l: '90 days' },
  { v: 'all', l: 'All time' },
];

const fmtDay = (s: string) =>
  s ? new Date(`${s}T00:00:00+05:30`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }) : '';

// ── Small UI bits ────────────────────────────────────────────────────────────
const SHELL: React.CSSProperties = { background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' };
const INNER: React.CSSProperties = { background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px' };

const Segmented = <T extends string>({ value, options, onChange }: { value: T; options: { v: T; l: string }[]; onChange: (v: T) => void }) => (
  <div style={{ display: 'inline-flex', gap: '1.8px', background: G.shellBg, borderRadius: '7.4px', padding: '2.8px', border: `1px solid ${G.line}`, flexWrap: 'wrap' }}>
    {options.map((o) => {
      const on = value === o.v;
      return (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          style={{
            padding: '4.6px 10px',
            borderRadius: '5.5px',
            fontSize: '0.66rem',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: on ? C.teal : 'transparent',
            color: on ? 'white' : 'rgba(17,85,80,0.6)',
            border: on ? `1px solid ${C.teal}` : '1px solid transparent',
          }}
        >
          {o.l}
        </button>
      );
    })}
  </div>
);

const SectionTitle: React.FC<{ text: string; extra?: React.ReactNode }> = ({ text, extra }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
    <div style={{ width: '3px', height: '14.8px', background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: '3px' }} />
    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>{text}</span>
    {extra}
  </div>
);

const DATE_INPUT: React.CSSProperties = {
  height: '31px',
  padding: '0 8px',
  border: `1px solid ${G.line}`,
  borderRadius: '7.4px',
  fontSize: '0.68rem',
  color: C.teal,
  background: 'white',
  fontFamily: 'inherit',
};

/** amount / count, or null when count is 0. */
const per = (paise: number, n: number) => (n > 0 ? Math.round(paise / n) : null);
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);

// ── Weekly trend (spend bars + cost-per-lead labels) ─────────────────────────
const WeeklyTrend: React.FC<{ weeks: WeekPoint[]; withGst: boolean }> = ({ weeks, withGst }) => {
  // Draw at the real width so text keeps its size on any screen.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!weeks.length)
    return (
      <div ref={box} style={{ fontSize: '0.7rem', color: C.muted, padding: '18px 0', textAlign: 'center' }}>
        No spend in this range.
      </div>
    );
  const amt = (w: WeekPoint) => (withGst ? w.amount_incl_gst_paise : w.amount_excl_gst_paise);
  const max = Math.max(...weeks.map(amt), 1);
  const H = 190;
  const top = 26;
  const bottom = 24;
  const slot = W / weeks.length;
  const bar = Math.min(46, slot * 0.6);
  return (
    <div ref={box}>
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} role="img" aria-label="Weekly spend">
      <line x1={0} x2={W} y1={H - bottom} y2={H - bottom} stroke={G.line} />
      {weeks.map((w, i) => {
        const a = amt(w);
        const h = ((H - top - bottom) * a) / max;
        const x = i * slot + (slot - bar) / 2;
        const y = H - bottom - h;
        const cpl = per(a, w.leads);
        return (
          <g key={w.week_start}>
            <title>{`Week of ${fmtDay(w.week_start)}: ${fmtINR(a)} · ${w.leads} leads${cpl !== null ? ` · ${fmtINR(cpl)}/lead` : ''}`}</title>
            <rect x={x} y={y} width={bar} height={Math.max(h, 1)} rx={4} fill="url(#spr-bar)" />
            <text x={x + bar / 2} y={y - 12} textAnchor="middle" fontSize="10" fontWeight={700} fill={C.teal}>
              {fmtINR(a, { decimals: 0 })}
            </text>
            {cpl !== null && (
              <text x={x + bar / 2} y={y - 2} textAnchor="middle" fontSize="8.5" fill="#15803d">
                {fmtINR(cpl, { decimals: 0 })}/lead
              </text>
            )}
            <text x={x + bar / 2} y={H - 8} textAnchor="middle" fontSize="9" fill={C.muted}>
              {fmtDay(w.week_start)}
            </text>
          </g>
        );
      })}
      <defs>
        <linearGradient id="spr-bar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.tealMid} />
          <stop offset="1" stopColor={C.teal} />
        </linearGradient>
      </defs>
    </svg>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SpendReport: React.FC = () => {
  const navigate = useNavigate();

  const [preset, setPreset] = useState<Preset>('month');
  const [range, setRange] = useState(presetRange('month'));
  const [groupBy, setGroupBy] = useState<GroupBy>('campaign');
  const [withGst, setWithGst] = useState(true);

  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const p = new URLSearchParams({ group_by: groupBy });
      if (range.from) p.set('date_from', range.from);
      if (range.to) p.set('date_to', range.to);
      const res = await apiClient.get<{ success: boolean; data: ReportData }>(`/v1/spend-report?${p}`);
      setData(res.data.data);
    } catch (e: any) {
      if (e?.response?.status === 403) setForbidden(true);
      else setError(spendError(e, 'Could not load the spend report.'));
    } finally {
      setLoading(false);
    }
  }, [groupBy, range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const amt = useCallback((r: ReportRow) => (withGst ? r.amount_incl_gst_paise : r.amount_excl_gst_paise), [withGst]);
  const t = data?.totals;
  const rows = data?.rows ?? [];

  const junkSpend = useMemo(() => rows.reduce((a, r) => a + (r.leads ? Math.round((amt(r) * r.invalid) / r.leads) : 0), 0), [rows, amt]);

  const pickPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') setRange(presetRange(p));
  };

  const openLeads = (r: ReportRow) => {
    if (groupBy === 'sync') navigate(`/leads?sheet_import_id=${encodeURIComponent(r.key)}`);
    else if (groupBy === 'sheet') navigate(`/leads?sheet_id=${encodeURIComponent(r.key)}`);
    else if (r.key !== 'none') navigate(`/leads?campaign=${encodeURIComponent(r.key)}`);
  };

  const exportCsv = () => {
    if (!data) return;
    const rupees = (p: number | null) => (p === null ? '' : (p / 100).toFixed(2));
    const head = [
      groupBy === 'campaign' ? 'Campaign' : groupBy === 'sheet' ? 'Sheet' : 'Sync',
      'Detail',
      `Spent (${withGst ? 'with' : 'without'} GST, INR)`,
      'Leads',
      'Cost per lead',
      'Reached',
      'Qualified',
      'Cost per qualified',
      'Converted',
      'Cost per conversion',
      'Invalid',
      'Junk %',
      'Syncs',
    ];
    const line = (r: ReportRow) => [
      r.label,
      r.sublabel ?? '',
      rupees(amt(r)),
      r.leads,
      rupees(per(amt(r), r.leads)),
      r.reached,
      r.qualified,
      rupees(per(amt(r), r.qualified)),
      r.converted,
      rupees(per(amt(r), r.converted)),
      r.invalid,
      pct(r.invalid, r.leads),
      r.syncs,
    ];
    const esc = (v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [head, ...rows.map(line), line(data.totals)].map((l) => l.map(esc).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `spend-report_${groupBy}_${range.from || 'all'}_${range.to || 'now'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Table cells ────────────────────────────────────────────────────────────
  const money = (p: number | null, color = '#374151') => (
    <span style={{ fontWeight: 700, color: p === null ? C.muted : color, whiteSpace: 'nowrap' }}>{p === null ? '—' : fmtINR(p)}</span>
  );
  const countWithCost = (n: number, cost: number | null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <span style={{ fontWeight: 700, color: '#374151' }}>{n}</span>
      <span style={{ fontSize: '0.6rem', color: cost === null ? C.muted : '#15803d', whiteSpace: 'nowrap' }}>{cost === null ? '—' : `${fmtINR(cost)} each`}</span>
    </div>
  );
  const CH: React.CSSProperties = { whiteSpace: 'nowrap' };

  // ── Header ─────────────────────────────────────────────────────────────────
  const header = (
    <div style={SHELL}>
      <div style={{ ...INNER, padding: '12px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.sub }}>Leads · Google Sheets · Admins only</div>
          <h1 style={{ margin: '3.7px 0 0', fontSize: '1.01rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '7px' }}>
            <IndianRupee size={16} color={C.tealMid} /> Spend Report
          </h1>
        </div>
        <button
          type="button"
          onClick={() => navigate(SHEET_IMPORTS_PATH)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            height: '31px',
            padding: '0 11px',
            background: G.shellBg,
            border: `1px solid ${G.line}`,
            borderRadius: '7.4px',
            color: C.teal,
            fontSize: '0.68rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={12} /> Sheet Imports
        </button>
      </div>
    </div>
  );

  if (forbidden) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {header}
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '36px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <Lock size={22} color={C.teal} />
            <div style={{ fontSize: '0.84rem', fontWeight: 700, color: C.teal }}>Admins only</div>
            <div style={{ fontSize: '0.7rem', color: C.sub }}>Spend and cost per lead are visible to admins only.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
      <style>{`
        .spr-dt .p-datatable-thead > tr > th { background:#fafbfb !important; color:#9ca3af !important; font-size:0.62rem !important; font-weight:700 !important; text-transform:uppercase !important; letter-spacing:0.5px !important; border-bottom:1px solid ${G.soft} !important; padding:9px 12px !important; }
        .spr-dt .p-datatable-tbody > tr > td { border-bottom:1px solid ${G.softer} !important; padding:9px 12px !important; vertical-align: top; }
        .spr-dt .p-datatable-tbody > tr:last-child > td { border-bottom:none !important; }
        .spr-dt .p-datatable-tfoot > tr > td { background:#fafbfb !important; border-top:1px solid ${G.soft} !important; padding:9px 12px !important; font-size:0.72rem; }
        .spr-dt .p-datatable-tbody > tr.spr-click { cursor: pointer; }
        .spr-dt .p-datatable-tbody > tr.spr-click:hover { background: #f7faf9 !important; }
        .spr-spin { animation: sprSpin 0.9s linear infinite; }
        @keyframes sprSpin { to { transform: rotate(360deg); } }
      `}</style>

      {header}

      {/* Filters */}
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
            <Segmented value={preset} options={PRESETS} onChange={pickPreset} />
            <input
              type="date"
              aria-label="From"
              value={range.from}
              max={range.to || undefined}
              onChange={(e) => {
                setPreset('custom');
                setRange((r) => ({ ...r, from: e.target.value }));
              }}
              style={DATE_INPUT}
            />
            <span style={{ fontSize: '0.66rem', color: C.muted }}>to</span>
            <input
              type="date"
              aria-label="To"
              value={range.to}
              min={range.from || undefined}
              onChange={(e) => {
                setPreset('custom');
                setRange((r) => ({ ...r, to: e.target.value }));
              }}
              style={DATE_INPUT}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
            <Segmented
              value={withGst ? 'incl' : 'excl'}
              onChange={(v) => setWithGst(v === 'incl')}
              options={[
                { v: 'incl', l: 'With GST' },
                { v: 'excl', l: 'Without GST' },
              ]}
            />
            <button
              type="button"
              onClick={load}
              disabled={loading}
              title="Refresh"
              style={{ height: '31px', width: '31px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: G.shellBg, border: `1px solid ${G.line}`, borderRadius: '7.4px', color: C.sub, cursor: 'pointer' }}
            >
              <RefreshCw size={12} className={loading ? 'spr-spin' : undefined} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <Banner tone={TONES.red} icon={<TriangleAlert size={15} />}>
          {error}
        </Banner>
      )}

      {(data?.syncs_without_amount ?? 0) > 0 && (
        <Banner
          tone={TONES.amber}
          icon={<TriangleAlert size={15} />}
          actions={
            <button
              type="button"
              onClick={() => navigate(SHEET_IMPORTS_PATH)}
              style={{ background: 'white', border: `1px solid ${TONES.amber.border}`, borderRadius: '7px', height: '28px', padding: '0 10px', color: TONES.amber.color, fontSize: '0.66rem', fontWeight: 700, cursor: 'pointer' }}
            >
              Add amounts
            </button>
          }
        >
          {data!.syncs_without_amount} pushed sync{data!.syncs_without_amount === 1 ? ' has' : 's have'} no amount in this range, so the totals below are
          lower than what you really spent.
        </Banner>
      )}

      {/* Tiles */}
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '12px', display: 'flex', gap: '6.5px', flexWrap: 'wrap' }}>
          <StatTile label={`Spent ${withGst ? '(with GST)' : '(without GST)'}`} value={t ? fmtINR(amt(t)) : '—'} />
          <StatTile label="Leads" value={t?.leads ?? '—'} />
          <StatTile label="Cost per lead" value={t ? fmtINR(per(amt(t), t.leads)) : '—'} color="#15803d" />
          <StatTile label="Cost per qualified" value={t ? fmtINR(per(amt(t), t.qualified)) : '—'} color={C.tealMid} />
          <StatTile label="Cost per conversion" value={t ? fmtINR(per(amt(t), t.converted)) : '—'} color={C.tealMid} />
          <StatTile
            label={`Spent on junk (${t ? pct(t.invalid, t.leads) : 0}%)`}
            value={t ? fmtINR(junkSpend) : '—'}
            color="#c62828"
          />
        </div>
      </div>

      {/* Weekly trend */}
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SectionTitle text="Weekly spend" extra={<span style={{ fontSize: '0.62rem', color: C.muted }}>by the day leads were added · hover a bar for details</span>} />
          <WeeklyTrend weeks={data?.weeks ?? []} withGst={withGst} />
        </div>
      </div>

      {/* Breakdown */}
      <div style={SHELL}>
        <div style={{ ...INNER, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${G.soft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9px', flexWrap: 'wrap' }}>
            <SectionTitle text="Breakdown" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
              <Segmented<GroupBy>
                value={groupBy}
                onChange={setGroupBy}
                options={[
                  { v: 'campaign', l: 'By campaign' },
                  { v: 'sheet', l: 'By sheet' },
                  { v: 'sync', l: 'By sync' },
                ]}
              />
              <button
                type="button"
                onClick={exportCsv}
                disabled={!rows.length}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: '31px',
                  padding: '0 11px',
                  background: rows.length ? C.teal : G.shellBg,
                  border: `1px solid ${rows.length ? C.teal : G.line}`,
                  borderRadius: '7.4px',
                  color: rows.length ? 'white' : C.muted,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  cursor: rows.length ? 'pointer' : 'default',
                }}
              >
                <Download size={12} /> CSV
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <DataTable
              value={rows}
              dataKey="key"
              loading={loading}
              className="spr-dt"
              style={{ fontSize: '0.74rem' }}
              rowClassName={(r: ReportRow) => (groupBy === 'campaign' && r.key === 'none' ? '' : 'spr-click')}
              onRowClick={(e) => openLeads(e.data as ReportRow)}
              emptyMessage={
                <div style={{ padding: '26px 0', textAlign: 'center', fontSize: '0.72rem', color: C.muted }}>
                  No amounts added for syncs in this range yet.
                </div>
              }
            >
              <Column
                header={groupBy === 'campaign' ? 'Campaign' : groupBy === 'sheet' ? 'Sheet' : 'Sync'}
                body={(r: ReportRow) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: '160px' }}>
                    <span style={{ fontWeight: 700, color: r.key === 'none' ? C.muted : C.teal }}>{r.label}</span>
                    {r.sublabel && <span style={{ fontSize: '0.6rem', color: C.muted, fontFamily: groupBy === 'sync' ? undefined : 'monospace' }}>{r.sublabel}</span>}
                  </div>
                )}
                footer={<strong style={{ color: C.teal }}>Total</strong>}
                headerStyle={CH}
              />
              <Column header="Spent" body={(r: ReportRow) => money(amt(r), C.teal)} footer={t ? money(amt(t), C.teal) : null} headerStyle={CH} />
              <Column header="Leads" body={(r: ReportRow) => <strong style={{ color: '#374151' }}>{r.leads}</strong>} footer={t?.leads} headerStyle={CH} />
              <Column header="Cost / lead" body={(r: ReportRow) => money(per(amt(r), r.leads), '#15803d')} footer={t ? money(per(amt(t), t.leads), '#15803d') : null} headerStyle={CH} />
              <Column header="Qualified" body={(r: ReportRow) => countWithCost(r.qualified, per(amt(r), r.qualified))} footer={t ? countWithCost(t.qualified, per(amt(t), t.qualified)) : null} headerStyle={CH} />
              <Column header="Converted" body={(r: ReportRow) => countWithCost(r.converted, per(amt(r), r.converted))} footer={t ? countWithCost(t.converted, per(amt(t), t.converted)) : null} headerStyle={CH} />
              <Column
                header="Junk"
                body={(r: ReportRow) => {
                  const p = pct(r.invalid, r.leads);
                  return (
                    <span style={{ fontWeight: 700, color: p >= 25 ? '#c62828' : p >= 10 ? '#b45309' : C.sub }}>
                      {p}% <span style={{ fontWeight: 500, color: C.muted, fontSize: '0.62rem' }}>({r.invalid})</span>
                    </span>
                  );
                }}
                footer={t ? `${pct(t.invalid, t.leads)}%` : null}
                headerStyle={CH}
              />
              {groupBy !== 'sync' && <Column header="Syncs" body={(r: ReportRow) => r.syncs} footer={t?.syncs} headerStyle={CH} />}
            </DataTable>
          </div>
          <div style={{ padding: '8px 14px', borderTop: `1px solid ${G.soft}`, fontSize: '0.6rem', color: C.muted, lineHeight: 1.6 }}>
            Leads = leads added when the sync was pushed. Qualified, converted and junk use each lead’s current status, so they keep
            updating as your team works the leads. Click a row to open those leads.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpendReport;
